import { observer } from "mobx-react";
import PolicyStore from "../../../../../stores/policy-store";
import policyListStore from "../../../../../stores/policy-list-store";
import currentPortalStore from "../../../../../../../stores/current-portal-store";
import { FEATURES } from "../../../../../../../models/Plan";
import { RESOLUTION_TYPE } from "../../../../../../resolutions/constants";
import { FormFieldLine } from "../../../../../../../components/form-field-line/form-field-line";
import { SelectDropdown } from "../../../../../../../components/inputs/select-dropdown/select-dropdown";
import { Note } from "../../../../../../../components/note/note";
import { AUTO_PROCESS_TRIGGER, INCLUDE_SHIPPING_COST_IN_REFUND } from "../../../../../../../models/User";
import { Toggle } from "../../../../../../../components/inputs/toggle/toggle";
import { processTypeOptionsList } from "../../../../../constants";
import ListLayout from "../../../../../../../components/layouts/list-layout/list-layout";
import RefundTriggerSettings from "../process-trigger-settings/refund-trigger-settings/refund-trigger-settings";
import { IValidationGroup } from "../../../../../../../hooks/validation-group/validation-group-hook";
import AHref from "../../../../../../../components/ahref/ahref";
import FontBody2 from "../../../../../../../components/fonts/font-body2/font-body2";
import { FONT_COLOR } from "../../../../../../../components/fonts/font-props";
import config from "../../../../../../../config";

interface IProps {
    policy: PolicyStore;
    validationGroup: IValidationGroup;
}

const RefundProcess: React.FC<IProps> = observer(({ policy, validationGroup}) => {
    const autoRefundAllowed = currentPortalStore.isFeatureAllowed(FEATURES.AUTO_REFUND);

    const isAutoRefund = policy.isRefundableResolutionSelected() && policy.isAutoRefund;
    const selectedIsAutoRefundOption = processTypeOptionsList().find(o => o.value === (isAutoRefund ? 1 : 0));

    const instantRefundProvider = policy.selectedResolutionRefundProvider;
    const instantRefundProviderDisplayName = instantRefundProvider.charAt(0).toUpperCase() + instantRefundProvider.slice(1)

    const isIncludeShippingCostInRefund = currentPortalStore.setting?.includeShippingCostInRefund ?? INCLUDE_SHIPPING_COST_IN_REFUND.NO;
    const isIncludeShippingCostInRefundToggleAllowed = isIncludeShippingCostInRefund === INCLUDE_SHIPPING_COST_IN_REFUND.SET_PER_POLICY_RULE;

    const resolutionsLink = `${config.apiUrl}/dashboard/settings/resolutions`;

    const refundProductDutiesNote = policyListStore.hasGlobalEDirectRefundIntegration
        ? 'When enabled, Global-e will include product duties in the customer refund total'
        : 'When enabled, product duties paid on returned items are included in the refund total.';

    return (
        <>
            {!!(autoRefundAllowed && policy.resolution) && (
                <>
                    {!!(policy.isRefundableResolutionSelected() && !policy.isCancelResolutionSelected()) && (
                        <>
                            {policy.selectedResolutionType !== RESOLUTION_TYPE.InstantRefundType
                                ? (
                                    <FormFieldLine
                                        label='Refund Process'
                                        note='Automate to issue the refund automatically when triggered.'
                                    >
                                        <ListLayout>
                                            <SelectDropdown
                                                value={selectedIsAutoRefundOption}
                                                options={processTypeOptionsList()}
                                                onChange={v => policy.setIsAutoRefund(!!v?.value)}
                                                isSearchable={false}
                                            />

                                            {!!isAutoRefund && <RefundTriggerSettings policy={policy} validationGroup={validationGroup} />}
                                        </ListLayout>
                                    </FormFieldLine>
                                )
                                : (
                                    <FormFieldLine
                                        label='Refund Process'
                                        note='When automatic, refund will be issued automatically for all items customers chose this policy rule as the resolution'
                                    >
                                        <Note body={`The refund will be issued by ${instantRefundProviderDisplayName}. The processing method depends on the service capabilities and your settings.`} />
                                    </FormFieldLine>
                                )
                            }
                            {policy.autoRefundTrigger === AUTO_PROCESS_TRIGGER.ITEMS_VALIDATED &&
                                <FormFieldLine
                                    label='Automated Item-Level Refunds'
                                    note='Automatically issue partial refunds at the item level according to item validation result.'
                                >
                                    <Toggle
                                        onChange={policy.setIssuePartialRefund}
                                        label={""}
                                        checked={policy.issuePartialRefund}
                                    />
                                </FormFieldLine>
                            }
                        </>
                    )}
                    {!!policy.isCancelResolutionSelected() && (
                        <FormFieldLine
                            label='Refund Process'
                            note='When automatic, refund will be issued automatically for all items customers chose this policy rule as the resolution'
                        >
                            <Note body={<>
                                This setting is defined as part of the Cancel Order resolution.<br />
                                When done editing this policy rule go to Settings {'>'} Resolutions <br />
                                and make sure that the resolution is properly set.

                                This setting is defined as part of the Cancel Order resolution. <br />
                                You can modify this on the 
                                <AHref href={resolutionsLink} target={'_blank'} ><FontBody2 color={FONT_COLOR.ACCENT1}>Resolutions</FontBody2></AHref> page.
                            </>
                            } />
                        </FormFieldLine>
                    )}
                </>
            )}

            {!!(policy.resolution && policy.isRefundableResolutionSelected() && isIncludeShippingCostInRefundToggleAllowed) && (
                <FormFieldLine
                    label='Include Shipping Costs in Refunds'
                    note='Enable to automatically refund the original shipping costs as part of the refund process.'
                >
                    <Toggle label='' checked={policy.includeShippingAmountInRefund} onChange={policy.setIncludeShippingAmountInRefund} />
                </FormFieldLine>
            )}

            {policy.shouldShowRefundProductDutiesToggle() && (
                <FormFieldLine
                    label='Refund product duties'
                    note={refundProductDutiesNote}
                >
                    <Toggle label='' checked={policy.refundProductDuties} onChange={policy.setRefundProductDuties} />
                </FormFieldLine>
            )}
        </>
    );
});

export default RefundProcess;